import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Modal, TextInput, Theme, ToggleButton } from '../../../components';
import { FoodType } from '../../../models';
import { Language } from '../../../models/Language';
import { foodTypeActions, foodTypeSelectors, userSelectors } from '../../../redux';

import './AddEditStore.scss';

interface Props {
  closeModal: () => void;
  isOpen: boolean;
  id?: string;
}

const FoodTypeModal: FC<Props> = ({ closeModal, id, isOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const foodTypes = useSelector(foodTypeSelectors.getFoodTypes);
  const isAddEditLoading = useSelector(foodTypeSelectors.isAddEditLoading);
  const userInfo = useSelector(userSelectors.getUserInfo);

  const [activeFoodType, setActiveFoodType] = useState<FoodType>();

  function save() {
    if (id) {
      dispatch(foodTypeActions.editFoodTypes(activeFoodType));
    } else {
      dispatch(foodTypeActions.addFoodTypes(activeFoodType));
    }

    handleClose();
  }

  function handleClose() {
    closeModal();
    setActiveFoodType(null);
  }

  function update(overrides: Partial<FoodType>) {
    setActiveFoodType({ ...activeFoodType, ...overrides });
  }

  function handleLanguageChange(lng: string, translation: string) {
    const index = activeFoodType?.translatedLabels?.findIndex(({ languageCode }) => lng === languageCode);

    if (index > -1) {
      let newArray = activeFoodType?.translatedLabels.slice();

      if (translation?.length) {
        newArray[index] = { languageCode: lng, translation };
      } else newArray.splice(index, 1);

      update({ translatedLabels: newArray });
    } else {
      update({
        translatedLabels: [
          ...(activeFoodType?.translatedLabels ?? []),
          {
            translation,
            languageCode: lng,
          },
        ],
      });
    }
  }

  useEffect(() => {
    setActiveFoodType(id ? foodTypes?.find(f => f.id === id) : undefined);
  }, [id]);

  return (
    <Modal title={t(id ? 'lblEditFoodItem' : 'lblAddFoodItem')} isOpen={isOpen} closeModal={() => closeModal()}>
      <div className='add-edit-container'>
        {userInfo.languages.map((lng: Language) => (
          <TextInput
            key={lng.language}
            type='text'
            label={t('lblFoodTypeName', {
              language: lng.language,
            })}
            placeholder={lng.language}
            onChange={(value: string) => handleLanguageChange(lng.code, value)}
            value={activeFoodType?.translatedLabels?.find(({ languageCode }) => languageCode === lng.code)?.translation}
          />
        ))}
        <TextInput
          type='text'
          label={t('lblArticleNumber')}
          placeholder={t('lblArticleNumber')}
          onChange={(ocisId: string) => update({ ocisId })}
          value={activeFoodType?.ocisId}
          isRequired={true}
        />
        <TextInput
          type='timer'
          min='0'
          label={t('lblMaxHoldingTime')}
          placeholder={t('lblMaxHoldingTime')}
          onChange={(maxHoldTime: string) => update({ maxHoldTime })}
          value={activeFoodType?.maxHoldTime}
          isRequired={true}
        />
        <ToggleButton
          label={t('lblFoodTypeActive')}
          isChecked={activeFoodType?.isActive}
          onClick={() => update({ isActive: !activeFoodType?.isActive })}
        />

        <div className='buttons'>
          <Button theme={Theme.INVERSE} onClickCallback={handleClose}>
            {t('lblCancel')}
          </Button>
          <Button
            theme={Theme.SECONDARY}
            onClickCallback={save}
            isLoading={isAddEditLoading}
            isDisabled={!activeFoodType?.ocisId || !activeFoodType?.maxHoldTime || !activeFoodType?.translatedLabels?.length}>
            {t('lblSave')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FoodTypeModal;
